package com.yogaaicoach.pages.workout.workout.ui

import com.yogaaicoach.pages.workout.data.MediaModificationService
import com.yogaaicoach.pages.workout.data.models.Exercise
import com.yogaaicoach.pages.workout.data.models.MediaNarrowResponse
import com.yogaaicoach.pages.workout.workout.ui.model.ExerciseVideoUrls
import com.yogaaicoach.platform.loadPreloadVideo
import com.yogaaicoach.platform.setVideoEndedListener
import com.yogaaicoach.platform.setVideoToSecond
import com.yogaaicoach.platform.swapVideos

class MediaActionController(
    private val mediaModificationService: MediaModificationService,
    private val platformLoadPreloadVideo: (url: String, onReady: () -> Unit) -> Unit = ::loadPreloadVideo,
    private val platformSwapVideos: () -> Unit = ::swapVideos,
    private val platformSetVideoEndedListener: (listener: (() -> Unit)?) -> Unit = ::setVideoEndedListener,
    private val platformSetVideoToSecond: (second: Int) -> Unit = ::setVideoToSecond,
) {
    private var videoActions = mutableMapOf<Int, () -> Unit>()
    private var audioActions = mutableMapOf<Int, () -> Unit>()
    private var redownloadUrlsAction: () -> Unit = {}

    /**
     * How long to hold the first frame before the current exercise starts playing. Videos that begin
     * mid-movement set this so the user has a beat to get into position. The exercise timer stays
     * frozen for this long, so the freeze does not eat into the exercise duration.
     */
    var startFreezeSeconds: Int = 0
        private set

    private val transitionBuilder = VideoTransitionBuilder(
        platformLoadPreloadVideo = platformLoadPreloadVideo,
        platformSwapVideos = platformSwapVideos,
        platformSetVideoEndedListener = platformSetVideoEndedListener,
        platformSetVideoToSecond = platformSetVideoToSecond,
        mediaModificationService = mediaModificationService,
    )

    fun loadActionsFor(
        exercise: Exercise,
        currentVideoUrls: ExerciseVideoUrls?,
        onRedownloadNeeded: () -> Unit = {},
    ) {
        clear()

        val video = exercise.baseData.video
        val totalDuration = exercise.durationInSeconds * exercise.repetitions
        val introDuration = video.introDuration ?: 0
        val outroDuration = video.outroDuration ?: 0

        if (video.hasMultipleParts() && currentVideoUrls != null) {
            videoActions.putAll(transitionBuilder.build(video, totalDuration, introDuration, outroDuration, currentVideoUrls, exercise.repetitions))
        } else {
            videoActions.putAll(mediaModificationService.getMapOfVideoModifications(video, exercise.durationInSeconds, exercise.repetitions))
        }

        audioActions.putAll(mediaModificationService.getMapOfAudioModifications(exercise.baseData.audio, exercise.durationInSeconds, exercise.repetitions))
        redownloadUrlsAction = onRedownloadNeeded
        startFreezeSeconds = (video.startFreezeSeconds ?: 0).coerceAtLeast(0)
    }

    fun beginStartFreeze() = mediaModificationService.pauseAllMedia()

    fun endStartFreeze() = mediaModificationService.resumeAllMedia()

    fun onTick(elapsedSeconds: Int, remainingSeconds: Int) {
        videoActions[elapsedSeconds]?.invoke()
        audioActions[elapsedSeconds]?.invoke()
        if (remainingSeconds == REDOWNLOAD_AT_REMAINING_SECONDS) {
            redownloadUrlsAction()
        }
    }

    private fun MediaNarrowResponse.hasMultipleParts() = introFilename != null || outroFilename != null

    private fun clear() {
        videoActions.clear()
        audioActions.clear()
        transitionBuilder.clear()
    }

    companion object {
        private const val REDOWNLOAD_AT_REMAINING_SECONDS = 20
    }
}
